import { useEffect, useState } from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ComputerDesktopIcon,
  AdjustmentsHorizontalIcon,
  ChartBarIcon,
  InformationCircleIcon,
  ArrowRightEndOnRectangleIcon
} from '@heroicons/react/24/outline'
import packageJson from '../../package.json'
import { useAuth } from 'auth/AuthContext'
import { MANAGEMENT_TABS, DASHBOARD_TABS } from 'constants/navs'
import AppBrandIcon from 'components/common/AppBrandIcon'
import LanguageSwitcher from 'components/common/LanguageSwitcher'
import { getDefaultPathByPermissions } from 'utils/nav.utils'
import { getLogoPreviewSrc } from 'utils/logo.utils'

const navSpan = 'text-xs opacity-0 group-hover:opacity-100 group-[.bg-gray-100]:opacity-100 transition-opacity duration-300'
const useActiveNav = () => {
  const location = useLocation()

  return ({ isActive }, pathPrefix = '') => {
    const isSectionActive = pathPrefix && location.pathname.startsWith(pathPrefix)
    return `group flex flex-col items-center py-3 w-full transition-all rounded-l-lg ${isActive || isSectionActive ? 'bg-gray-100 text-greenPrimary' : ''}`
  }
}

const NavSidebar = () => {
  const { logout, user } = useAuth()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isActiveNav = useActiveNav()
  const logoSrc = getLogoPreviewSrc(user?.logo)
  const [showIconFallback, setShowIconFallback] = useState(false)

  useEffect(() => {
    setShowIconFallback(false)
  }, [logoSrc])

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const getNavLinkClass = pathPrefix => props => `${isActiveNav(props, pathPrefix)} group`

  return (
    <aside className='fixed top-0 left-0 h-screen w-24 z-30 flex flex-col justify-between items-center pt-5 pl-2 text-white'>
      {showIconFallback
        ? (
          <AppBrandIcon size='md' className='hover:scale-105 transition-transform duration-200' />
          )
        : (
          <div className='w-16 h-16 rounded-lg bg-white border border-gray-200 p-1 flex items-center justify-center shadow-sm hover:scale-105 transition-transform duration-200'>
            <img
              src={logoSrc}
              alt='GreenAmex logo'
              className='max-w-full max-h-full object-contain'
              onError={() => setShowIconFallback(true)}
            />
          </div>
          )}
      <nav className='flex flex-col flex-grow w-full mt-5'>
        <ul className='flex flex-col flex-grow w-full space-y-2'>
          <li>
            <NavLink
              to={getDefaultPathByPermissions(DASHBOARD_TABS, '/dashboard', user.permissions)}
              className={getNavLinkClass('/dashboard')}
            >
              <ComputerDesktopIcon className='w-7 h-7 mb-1' />
              <span className={navSpan}>{t('nav.sidebar.dashboard')}</span>
            </NavLink>
          </li>
          <li>
            <NavLink
              to={getDefaultPathByPermissions(MANAGEMENT_TABS, '/management', user.permissions)}
              className={getNavLinkClass('/management')}
            >
              <AdjustmentsHorizontalIcon className='w-7 h-7 mb-1' />
              <span className={navSpan}>{t('nav.sidebar.management')}</span>
            </NavLink>
          </li>
          <li>
            <NavLink to='/reports' className={getNavLinkClass('/reports')}>
              <ChartBarIcon className='w-7 h-7 mb-1' />
              <span className={navSpan}>{t('nav.sidebar.reports')}</span>
            </NavLink>
          </li>
          <li>
            <NavLink to='/help' className={getNavLinkClass('/help')}>
              <InformationCircleIcon className='w-7 h-7 mb-1' />
              <span className={navSpan}>{t('nav.sidebar.help')}</span>
            </NavLink>
          </li>
          <li>
            <button
              onClick={handleLogout}
              className='flex flex-col items-center py-3 w-full transition-all rounded-l-lg hover:bg-gray-100 hover:text-greenPrimary'
            >
              <ArrowRightEndOnRectangleIcon className='w-7 h-7 mb-1' />
              <span className='text-xs'>{t('nav.sidebar.logout')}</span>
            </button>
          </li>
        </ul>
        <div className='flex justify-center px-2 mt-2'>
          <LanguageSwitcher variant='dark' />
        </div>
        <span className='block text-center text-[0.7rem] text-white/60 p-4 font-bold mt-auto'>
          {user.firstName} {user.lastName}
        </span>
        <span className='block text-center text-[0.7rem] text-white/60 p-4 font-bold mt-auto'>
          v {packageJson.version}
        </span>
      </nav>
    </aside>
  )
}

export default NavSidebar
